// src/components/shared/WelcomeModal.tsx

import { useState } from 'react'

// The real Social Brew mark — the same file the sign-up page and the Brew
// button use. It has a white background, so it is composited with
// mix-blend-mode: multiply against the cream sheet, exactly as AuthForm does.
const LOGO_SRC = '/icon-512.png'

interface Props {
  username: string
  onClose: () => void
  onBrew: () => void
}

// The three steps a new user needs before they can do anything useful. Each one
// names the button they actually tap, so the screen points somewhere rather than
// just describing the product.
const STEPS: { n: number; icon?: string; title: string; desc: string; demo?: boolean }[] = [
  {
    n: 1,
    icon: '📍',
    title: 'Find a shop',
    desc: 'Tap Discover to see the independent shops near you — wherever you happen to be.',
  },
  {
    n: 2,
    // No emoji here — the mug below the text is this step's picture, and a
    // teapot sitting beside a mug only muddled it.
    title: 'Fill the mug',
    desc: 'Tap the cup in the middle, then swipe up the mug to show how good it was. Add your drink, a photo and the vibe.',
    demo: true,
  },
  {
    n: 3,
    icon: '❤️',
    title: 'Share the good ones',
    desc: 'Your brew lands on Home for your friends to see, and quietly builds your own coffee map.',
  },
]

// The Social Brew mug filling itself on a loop. The swipe is a movement, so a
// still picture cannot teach it — this shows the gesture instead of describing
// it. Uses the brand mark rather than a generic cup, and is drawn rather than
// screenshotted so it can never fall out of date with the real screen.
// Sits under step two's text rather than beside it, so the copy keeps the same
// width as the other steps and the mug is big enough for the pour to read.
function MugDemo() {
  const SIZE = 76
  return (
    <div className="flex items-end gap-2 mt-2.5" aria-hidden="true">
      <div className="relative flex-shrink-0" style={{ width: SIZE, height: SIZE }}>
        {/* Dimmed mark holds the shape while the cup is empty. Kept strong
            enough to read as an empty mug rather than a rendering fault. */}
        <img
          src={LOGO_SRC} alt="" width={SIZE} height={SIZE}
          style={{ display: 'block', mixBlendMode: 'multiply', opacity: 0.32 }}
        />
        {/* The same mark revealed from the base upward, so the logo itself
            appears to fill. A flat image cannot be poured into, but it can be
            uncovered — which reads the same way and keeps the real artwork. */}
        <img
          src={LOGO_SRC} alt="" width={SIZE} height={SIZE}
          className="sb-welcome-fill"
          style={{ position: 'absolute', inset: 0, display: 'block', mixBlendMode: 'multiply' }}
        />
      </div>
      {/* Arrows echo the direction of the swipe */}
      <div className="flex flex-col items-center pb-3">
        <svg width="18" height="11" viewBox="0 0 16 10" className="sb-welcome-arrow">
          <polyline points="2,8 8,2 14,8" fill="none" stroke="#c8853a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className="text-caramel text-xs font-semibold mt-0.5">Swipe up</p>
      </div>
    </div>
  )
}

// Message a user can hand to a shop they love. Claiming is what switches on
// digital punch cards, so this is the step that unlocks them for the user too.
const SHOP_INVITE_TEXT =
  "I use Social Brew to find independent coffee shops. You can claim your shop's page there for free — it lets you run digital punch cards for regulars like me ☕"

const SHOP_INVITE_URL = 'https://socialbrewapp.com'

export default function WelcomeModal({ username, onClose, onBrew }: Props) {
  const [closing, setClosing] = useState(false)
  const [copied, setCopied] = useState(false)

  function handleClose() {
    setClosing(true)
    setTimeout(onClose, 300)
  }

  function handleBrew() {
    setClosing(true)
    setTimeout(onBrew, 300)
  }

  // Hands the invite to the phone's own share sheet so it can go out by text,
  // WhatsApp or email. Falls back to the clipboard on desktop, where there is
  // no share sheet to open.
  async function handleInviteShop() {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Social Brew', text: SHOP_INVITE_TEXT, url: SHOP_INVITE_URL })
      } catch { /* dismissed */ }
      return
    }
    try {
      await navigator.clipboard.writeText(`${SHOP_INVITE_TEXT} ${SHOP_INVITE_URL}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch { /* clipboard unavailable */ }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(8,4,1,0.82)' }}
      onClick={handleClose}
    >
      <div
        className="w-full max-w-lg mx-auto rounded-t-3xl overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(160deg, #fdfaf5 0%, #f5ead8 100%)',
          // Own stacking context, so the logo's multiply blend composites against
          // this cream sheet rather than the near-black backdrop behind it —
          // without this the mark blends to black. Same guard BottomNav uses.
          isolation: 'isolate',
          maxHeight: '88vh',
          transform: closing ? 'translateY(100%)' : 'translateY(0)',
          transition: 'transform 0.3s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Keyframes for the mug demo. Kept local to this component so no global
            stylesheet has to change, and switched off for anyone who has asked
            their device to reduce motion. */}
        <style>{`
          @keyframes sbWelcomeFill {
            0%, 8%    { clip-path: inset(100% 0 0 0); }
            45%, 72%  { clip-path: inset(26% 0 0 0); }
            100%      { clip-path: inset(100% 0 0 0); }
          }
          @keyframes sbWelcomeArrow {
            0%, 100%  { transform: translateY(2px); opacity: 0.25; }
            45%       { transform: translateY(-2px); opacity: 1; }
          }
          .sb-welcome-fill {
            animation: sbWelcomeFill 3.2s ease-in-out infinite;
          }
          .sb-welcome-arrow {
            animation: sbWelcomeArrow 3.2s ease-in-out infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .sb-welcome-fill { animation: none; clip-path: inset(26% 0 0 0); }
            .sb-welcome-arrow { animation: none; opacity: 0.6; }
          }
        `}</style>

        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-coffee-300 opacity-40" />
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-6 pt-2 pb-4 flex-1">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <img
                src={LOGO_SRC} alt="Social Brew" width={72} height={72}
                style={{ display: 'block', mixBlendMode: 'multiply' }}
              />
            </div>
            <h1 className="font-display text-2xl font-bold text-coffee-900 leading-tight">
              You found your people.
            </h1>
            <p className="text-coffee-500 text-sm mt-2">
              Welcome, <span className="font-semibold text-caramel">{username}</span>
            </p>
          </div>

          {/* Founder message */}
          <div
            className="rounded-2xl p-4 mb-6"
            style={{ background: 'rgba(200,133,58,0.08)', border: '1px solid rgba(200,133,58,0.15)' }}
          >
            <p className="text-coffee-700 text-sm leading-relaxed">
              Hey, I'm Moses. I built Social Brew so you can find the independent coffee shops worth your time, rate what you drink, and pass the good ones on to your friends.
            </p>
            <p className="text-coffee-600 text-sm leading-relaxed mt-2">
              The cherry on top: every rating helps a shop make you a better cup, and puts them in front of people who'd never have found them otherwise. <span className="font-semibold text-coffee-800">No chains. No algorithms.</span> Just real people and real coffee.
            </p>
            <p className="text-caramel font-semibold text-sm mt-3">
              — Moses ☕
            </p>
          </div>

          {/* How it works */}
          <p className="text-coffee-800 font-display font-bold text-base mb-3">
            How Social Brew works
          </p>
          <div className="space-y-3 mb-6">
            {STEPS.map(s => (
              <div key={s.n} className="flex items-start gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #c8853a, #9b5e1a)' }}
                >
                  {s.n}
                </div>
                <div className="flex-1">
                  <p className="text-coffee-800 font-semibold text-sm">
                    {s.icon && <span className="mr-1.5">{s.icon}</span>}{s.title}
                  </p>
                  <p className="text-coffee-500 text-xs leading-relaxed mt-0.5">{s.desc}</p>
                  {s.demo && <MugDemo />}
                </div>
              </div>
            ))}
          </div>

          {/* Punch card ask — the one favour we ask of a brand new user */}
          <div
            className="rounded-2xl p-4 mb-4"
            style={{ background: 'rgba(200,133,58,0.06)', border: '1px solid rgba(200,133,58,0.12)' }}
          >
            <p className="text-coffee-800 font-semibold text-sm mb-1">
              🎟️ Got a shop you already love?
            </p>
            <p className="text-coffee-600 text-xs leading-relaxed">
              Ask them to claim their page — it's free. Once they do, you can collect digital punch cards there and earn free drinks.
            </p>
            <button
              onClick={handleInviteShop}
              className="mt-3 w-full py-2.5 rounded-xl text-sm font-semibold transition-colors"
              style={{
                background: 'rgba(200,133,58,0.12)',
                border: '1px solid rgba(200,133,58,0.25)',
                color: '#9b5e1a',
              }}
            >
              {copied ? 'Copied — paste it to them ✓' : 'Send them the invite →'}
            </button>
          </div>
        </div>

        {/* Pinned buttons */}
        <div className="px-6 pb-8 pt-3 flex-shrink-0 space-y-2" style={{ borderTop: '1px solid rgba(200,133,58,0.1)' }}>
          <button
            onClick={handleBrew}
            className="w-full py-4 rounded-2xl text-white font-bold text-base"
            style={{ background: 'linear-gradient(135deg, #c8853a, #9b5e1a)', boxShadow: '0 4px 16px rgba(200,133,58,0.3)' }}
          >
            Start my coffee journey →
          </button>
          <button
            onClick={handleClose}
            className="w-full py-3 rounded-2xl text-coffee-500 font-medium text-sm"
          >
            Explore the feed first
          </button>
        </div>
      </div>
    </div>
  )
}
